import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UsersService, UserItem } from '../../../../services/users.service';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import { AlertService } from '../../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../../hooks/use-request.hook';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Icon from '../../../../components/forms/icon.component';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import Footer from '../../../../components/layout/footer.component';
import FilterBar from '../../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../../components/cards/header-stats.component';
import TableUsers from './table-users';
import UserFormModal from './user-form.modal';

const UsersPage: React.FC = () => {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const { isSuperAdmin, canWrite } = usePermissions();
  const canManage = canWrite('users');

  const [usersRaw, setUsersRaw] = useState<UserItem[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserItem | null>(null);

  const userRows = useMemo<UserItem[]>(() => {
    return Array.isArray(usersRaw) ? usersRaw : [];
  }, [usersRaw]);

  const roleOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return userRows.reduce<FilterOption[]>((acc, r) => {
      (r.roles || []).forEach((role) => {
        if (role && !seen.has(role)) {
          seen.add(role);
          acc.push({ value: role, label: role.toUpperCase() });
        }
      });
      return acc;
    }, []);
  }, [userRows]);

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];

    if (roleOptions.length > 0) {
      defs.push({
        key: 'role',
        label: t('users-page.profiles-label'),
        icon: <Icon icon="ki-security-user" size="fs-7" />,
        options: roleOptions,
        multi: false,
        searchable: true
      });
    }

    defs.push({
      key: 'status',
      label: t('users-page.status'),
      icon: <Icon icon="ki-shield-search" size="fs-7" />,
      options: [
        { value: 'true', label: t('users-page.rolePermissions.enabled') },
        { value: 'false', label: t('users-page.rolePermissions.disabled') }
      ],
      multi: false,
      searchable: false
    });

    return defs;
  }, [roleOptions, t]);

  const displayRows = useMemo(() => {
    let rows = userRows;

    if (filters.role) {
      rows = rows.filter((r) => (r.roles || []).includes(String(filters.role)));
    }

    if (filters.status) {
      const targetStatus = filters.status === 'true';
      rows = rows.filter((r) => r.enabled === targetStatus);
    }

    return [...rows].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [userRows, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => {
    setFilters(next);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await call(() => UsersService.listUsers({ page: 1, pageSize: 100 }));
      setUsersRaw(response?.items ?? []);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteUser = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = UsersService.deleteUser(String(id));
        await AlertService.toastLoading(
          deletePromise,
          'Eliminando usuario...',
          t('users-page.delete-user-success'),
          t('users-page.user-error')
        );
        fetchUsers();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchUsers, t]
  );

  const handleResetPassword = useCallback(
    async (row: UserItem) => {
      try {
        const response = await UsersService.resetPassword(row.id, false);
        await AlertService.success(
          t('users-page.recover-password-success'),
          `${t('users-page.modal-add-user.password')} <b>${response.tempPassword}</b>`
        );
      } catch (error: any) {
        AlertService.toastError(error?.error || t('users-page.recover-password-error'));
      }
    },
    [t]
  );

  const handleOpenCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((row: UserItem) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const handleRefresh = useCallback(async () => {
    await fetchUsers();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: t('users-page.total-users'),
                    value: displayRows.length,
                    icon: 'ki-duotone ki-profile-user fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: t('users-page.statistics.active'),
                    value: displayRows.filter((r) => r.enabled).length,
                    icon: 'ki-duotone ki-check-circle fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  canManage ? (
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={handleOpenCreate}
                    >
                      <Icon icon="ki-plus" size="fs-4 me-1" />
                      {t('users-page.add-users-btn')}
                    </button>
                  ) : undefined
                }
              />
              {userRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={[]}
                  selectedChannel=""
                  onChannelChange={() => {}}
                />
              )}
              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <TableUsers
                    rows={displayRows}
                    isLoading={isRequesting}
                    canWrite={canManage}
                    canManageSuperAdmin={isSuperAdmin}
                    onEdit={handleOpenEdit}
                    onDelete={handleDeleteUser}
                    onResetPassword={handleResetPassword}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <UserFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchUsers}
        editing={editing}
        canAssignSuperAdmin={isSuperAdmin}
      />
    </div>
  );
};

export default UsersPage;
